import React, { useState, useEffect } from 'react';
import {
  Modal,
  Typography,
  TextField,
  Box,
  Button,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  getFirestore,
  doc,
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  getDoc
} from 'firebase/firestore';
import { auth } from './firebaseConfig';
import { COLLECTION_PATHS, CUSTOMER_STATUS } from '../utils/constants';

import ConfirmUpdateModal from './ConfirmUpdateModal';
import ConflictResolutionModal from './ConflictResolutionModal';
import CustomerSelectionTable from './CustomerSelectionTable';
import TransferCustomerModal from './TransferCustomerModal';
import { toTurkishUpperCase } from '../utils/turkishUtils';

/**
 * Çalışma süresini dakikaya çeviren fonksiyon - Birden fazla format destekler
 * iOS HopsPlus ile AYNI MANTIK
 */
const parseWorkingTime = (timeString) => {
  if (!timeString || timeString.toString().trim() === '') return 0;

  const input = timeString.toString().trim();

  // Format 1: "2:30" veya "1:00" (HH:MM)
  if (input.includes(':') && !input.toLowerCase().includes('saat')) {
    const parts = input.split(':');
    if (parts.length === 2) {
      const hours = parseInt(parts[0].trim(), 10) || 0;
      const minutes = parseInt(parts[1].trim(), 10) || 0;
      return hours * 60 + minutes;
    }
  }

  // Format 2: "1 saat 0 dakika" veya "2 saat 30 dakika"
  if (input.toLowerCase().includes('saat')) {
    let hours = 0;
    let minutes = 0;
    const saatMatch = input.toLowerCase().match(/(\d+)\s*saat/);
    if (saatMatch) hours = parseInt(saatMatch[1], 10) || 0;
    const dakikaMatch = input.toLowerCase().match(/(\d+)\s*(dakika|dk)/);
    if (dakikaMatch) minutes = parseInt(dakikaMatch[1], 10) || 0;
    return hours * 60 + minutes;
  }

  // Format 3: "8.5" veya "8,5" (ondalık saat)
  const normalized = input.replace(',', '.');
  const decimalHours = parseFloat(normalized);
  if (!isNaN(decimalHours) && normalized.includes('.')) {
    return Math.round(decimalHours * 60);
  }

  // Format 4: Sadece sayı (dakika olarak)
  const justNumber = parseInt(input, 10);
  if (!isNaN(justNumber)) return justNumber;

  return 0;
};

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  backgroundColor: '#ffffff',
  color: '#000000',
  boxShadow: theme.shadows[5],
  padding: theme.spacing(4),
  maxHeight: '90vh',
  overflowY: 'auto',
  border: '1px solid #ccc',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
  width: '30px',
  height: '30px',
}));

const normalizeString = (str) => {
  return str ? str.trim().replace(/\s+/g, ' ') : '';
};

const EditCustomerModal = ({
  isOpen,
  onClose,
  selectedCustomer,
  setSelectedCustomer,
  approvedCustomers,
  setAlertOpen,
  setError,
  setSuccessMessage,
}) => {
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editCariCode, setEditCariCode] = useState('');
  const [updateChanges, setUpdateChanges] = useState([]);
  const [isConfirmUpdateOpen, setIsConfirmUpdateOpen] = useState(false);
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const [conflictingCustomer, setConflictingCustomer] = useState(null);
  const [isCustomerSelectionOpen, setIsCustomerSelectionOpen] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  // 1. State ekle: veri aktarma popupı için
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  // Özel saatlik fiyat alanı için state (normal makineler)
  const [specialHourlyRate, setSpecialHourlyRate] = useState('');
  // Özel sefer fiyatları için state (kamyonlar)
  const [specialIcSeferFiyati, setSpecialIcSeferFiyati] = useState('');
  const [specialDisSeferFiyati, setSpecialDisSeferFiyati] = useState('');
  // Vergi numarası alanı için state (kurumsal müşteriler için)
  const [editTaxNumber, setEditTaxNumber] = useState('');

  // Eski müşteri verilerini tutan state
  const [oldCustomerData, setOldCustomerData] = useState(null);

  // Tüm müşterileri state'e al
  const [nameConflict, setNameConflict] = useState(false);
  const [codeConflict, setCodeConflict] = useState(false);

  // 1. Çakışan müşteri adını ve cari kodunu bulmak için state ekle
  const [conflictingNameCustomer, setConflictingNameCustomer] = useState(null);
  const [conflictingCodeCustomer, setConflictingCodeCustomer] = useState(null);

  // Müşteri adı ve cari kodu değiştikçe çakışma kontrolü
  useEffect(() => {
    // Mevcut müşteri hariç tüm müşterileri getir
    // (ör: props.customers veya API'den çekiyorsan oradan al)
    // Burada örnek olarak props.customers kullanıyorum
    if (approvedCustomers) {
      const selectedId = selectedCustomer?.id;
      setAllCustomers(selectedId ? approvedCustomers.filter(c => c.id !== selectedId) : approvedCustomers);
    }
  }, [approvedCustomers, selectedCustomer?.id]);

  // 2. Müşteri adı değiştikçe kontrol
  useEffect(() => {
    if (!editName.trim()) {
      setNameConflict('empty');
      setConflictingNameCustomer(null);
    } else {
      const conflictCustomer = allCustomers.find(c => c['Müşteri Adı']?.trim().toLowerCase() === editName.trim().toLowerCase());
      setNameConflict(conflictCustomer ? 'conflict' : false);
      setConflictingNameCustomer(conflictCustomer || null);
    }
  }, [editName, allCustomers]);

  // 3. Cari kodu değiştikçe kontrol
  useEffect(() => {
    if (!editCariCode.trim()) {
      setCodeConflict('empty');
      setConflictingCodeCustomer(null);
    } else {
      const conflictCustomer = allCustomers.find(c => (c.cariCode || '').trim().toLowerCase() === (editCariCode || '').trim().toLowerCase());
      setCodeConflict(conflictCustomer ? 'conflict' : false);
      setConflictingCodeCustomer(conflictCustomer || null);
    }
  }, [editCariCode, allCustomers]);

  useEffect(() => {
    if (selectedCustomer) {
      // Modal ilk açıldığında orijinal verileri kaydet
      setOldCustomerData({
        'Müşteri Adı': selectedCustomer['Müşteri Adı'] || '',
        'cariCode': selectedCustomer['cariCode'] || ''
      });

      setEditName(toTurkishUpperCase(selectedCustomer['Müşteri Adı'] || ''));
      setEditPhone(selectedCustomer['Telefon'] || '');
      setEditEmail(selectedCustomer['E-posta'] || '');
      setEditCariCode(selectedCustomer['cariCode'] || '');
      setSpecialHourlyRate(
        selectedCustomer['Özel Saatlik Fiyat'] !== undefined && selectedCustomer['Özel Saatlik Fiyat'] !== null
          ? String(selectedCustomer['Özel Saatlik Fiyat'])
          : ''
      );
      setSpecialIcSeferFiyati(
        selectedCustomer['Özel İç Sefer Fiyatı'] !== undefined && selectedCustomer['Özel İç Sefer Fiyatı'] !== null
          ? String(selectedCustomer['Özel İç Sefer Fiyatı'])
          : ''
      );
      setSpecialDisSeferFiyati(
        selectedCustomer['Özel Dış Sefer Fiyatı'] !== undefined && selectedCustomer['Özel Dış Sefer Fiyatı'] !== null
          ? String(selectedCustomer['Özel Dış Sefer Fiyatı'])
          : ''
      );
      setEditTaxNumber(selectedCustomer['vergiNumarasi'] || '');
    }
  }, [selectedCustomer, setError, setAlertOpen]);

  useEffect(() => {
    const fetchAllCustomers = async () => {
      const db = getFirestore();
      const customersRef = collection(db, `users/${auth.currentUser?.email}/customerList`);
      const customersSnapshot = await getDocs(customersRef);
      const customersData = customersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllCustomers(customersData);
    };

    fetchAllCustomers();
  }, []);

  useEffect(() => {
    const fetchApprovedCustomers = async () => {
      const db = getFirestore();
      const customersRef = collection(db, `users/${auth.currentUser?.email}/customerList`);
      const q = query(
        customersRef,
        where('Onay', '==', 'Onaylandı')
      );

      try {
        const snapshot = await getDocs(q);
        const approvedData = snapshot.docs.map(doc => ({
          id: doc.id,
          'Müşteri Adı': doc.data()['Müşteri Adı'] || '',
          'Onay': doc.data()['Onay'] || '',
          'cariCode': doc.data()['cariCode'] || '',
          'Şantiye': doc.data()['Şantiye'] || false,
          'parentId': doc.data().parentId || null,
        }));
        setAllCustomers(approvedData);
        console.log('Onaylanmış müşteriler:', approvedData);
      } catch (error) {
        console.error('Müşteriler getirilirken hata:', error);
      }
    };

    fetchApprovedCustomers();
  }, []);

  const handleCloseModal = () => {
    // Modal kapanırken değişiklikleri geri al
    if (oldCustomerData && selectedCustomer) {
      // Form alanlarını eski değerlere döndür
      setEditName(toTurkishUpperCase(oldCustomerData['Müşteri Adı'] || ''));
      setEditCariCode(oldCustomerData['cariCode'] || '');
      setEditPhone(selectedCustomer['Telefon'] || '');
      setEditEmail(selectedCustomer['E-posta'] || '');
      setEditTaxNumber(selectedCustomer['vergiNumarasi'] || '');
    }
    onClose();
  };

  const [isConfirmChangeModalOpen, setIsConfirmChangeModalOpen] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(false);

  const handleUpdateCustomer = async () => {
    // Eğer müşteri adı veya cari kodu değişmişse onay iste
    if (
      (oldCustomerData && oldCustomerData['Müşteri Adı'] !== editName) ||
      (oldCustomerData && oldCustomerData['cariCode'] !== editCariCode)
    ) {
      setIsConfirmChangeModalOpen(true);
      setPendingUpdate(true);
      return;
    }
    // Değişiklik yoksa veya onaylandıysa direkt güncelle
    await checkConflictsAndUpdate();
  };

  const handleConfirmChange = async () => {
    setIsConfirmChangeModalOpen(false);
    setPendingUpdate(false);
    await checkConflictsAndUpdate();
  };

  const handleCancelChange = () => {
    setIsConfirmChangeModalOpen(false);
    setPendingUpdate(false);
  };

  const checkConflictsAndUpdate = async () => {
    const db = getFirestore();

    try {
      console.log('🔍 Çakışma kontrolü başlıyor:', {
        editName: editName.trim(),
        editCariCode: editCariCode.trim(),
        originalName: oldCustomerData?.['Müşteri Adı'],
        originalCariCode: oldCustomerData?.['cariCode'],
        selectedCustomerId: selectedCustomer.id
      });

      const customerListRef = collection(db, `users/${auth.currentUser?.email}/customerList`);
      
      // Müşteri adı çakışması kontrolü
      const nameConflictQuery = query(
        customerListRef,
        where('Müşteri Adı', '==', editName.trim()),
        where('__name__', '!=', selectedCustomer.id) // Kendi kendisiyle çakışma olmasın
      );
      
      // Cari kod çakışması kontrolü  
      const cariCodeConflictQuery = query(
        customerListRef,
        where('cariCode', '==', editCariCode.trim()),
        where('__name__', '!=', selectedCustomer.id) // Kendi kendisiyle çakışma olmasın
      );

      const [nameConflictSnapshot, cariCodeConflictSnapshot] = await Promise.all([
        getDocs(nameConflictQuery),
        getDocs(cariCodeConflictQuery),
      ]);

      // Eğer çakışma varsa modal aç
      if (!nameConflictSnapshot.empty || !cariCodeConflictSnapshot.empty) {
        const conflictingDoc = !nameConflictSnapshot.empty
          ? nameConflictSnapshot.docs[0]
          : cariCodeConflictSnapshot.docs[0];

        console.log('🚨 Çakışma bulundu, çakışma çözümleme modalı açılıyor');
        setConflictingCustomer({ ...conflictingDoc.data(), id: conflictingDoc.id });
        setIsConflictModalOpen(true);
        return;
      }

      console.log('✅ Çakışma tespit edilmedi, güncelleme devam ediyor');
      proceedWithUpdate();
    } catch (error) {
      console.error('Çakışma kontrolü sırasında hata:', error);
      setError(`Çakışma kontrolü sırasında bir hata oluştu: ${error.message}`);
      setAlertOpen(true);
    }
  };

  const proceedWithUpdate = async () => {
    const changes = [];
    const db = getFirestore();
    const batch = writeBatch(db);
    const userEmail = auth.currentUser?.email;

    if (!userEmail) {
      setError('Kullanıcı oturumu bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      // Müşteri bilgilerini güncelle
      const customerRef = doc(db, COLLECTION_PATHS.USER_CUSTOMERS(userEmail), selectedCustomer.id);
      const updateData = {
        'Müşteri Adı': editName.trim(),
        'Telefon': editPhone.trim(),
        'E-posta': editEmail.trim(),
        'cariCode': editCariCode.trim(),
        'Onay': CUSTOMER_STATUS.APPROVED,
        'updatedAt': new Date().toISOString(),
        'updatedBy': userEmail,
        ...(specialHourlyRate !== '' ? { 'Özel Saatlik Fiyat': Number(specialHourlyRate) } : { 'Özel Saatlik Fiyat': null }),
        ...(specialIcSeferFiyati !== '' ? { 'Özel İç Sefer Fiyatı': Number(specialIcSeferFiyati) } : { 'Özel İç Sefer Fiyatı': null }),
        ...(specialDisSeferFiyati !== '' ? { 'Özel Dış Sefer Fiyatı': Number(specialDisSeferFiyati) } : { 'Özel Dış Sefer Fiyatı': null }),
        'vergiNumarasi': editTaxNumber.replace(/\D/g, '').trim() // Vergi numarası (kurumsal müşteri eşleşmesi için)
      };
      batch.update(customerRef, updateData);

      // İlgili puantajları güncelle - timeSheets koleksiyonunu kullan
      const timeSheetsRef = collection(db, `users/${userEmail}/timeSheets`);
      const puantajQuery = query(
        timeSheetsRef,
        where('Müşteri Adı', '==', selectedCustomer['Müşteri Adı'])
      );
      
      const puantajSnapshot = await getDocs(puantajQuery);
      
      // Tüm puantajları güncelle
      puantajSnapshot.forEach((puantajDoc) => {
        batch.update(puantajDoc.ref, {
          'Müşteri Adı': editName.trim()
        });
      });

      // ⭐ YENİ: Müşteri adı değiştiğinde debt/current subcollection'ı da güncelle
      console.log('🔍 [DEBT UPDATE] Kontrol başlıyor...');
      console.log('   📍 Eski ad:', selectedCustomer['Müşteri Adı']);
      console.log('   📍 Yeni ad:', editName.trim());
      console.log('   📍 Müşteri ID:', selectedCustomer?.id);
      
      if (selectedCustomer['Müşteri Adı'] !== editName.trim() && selectedCustomer?.id) {
        console.log('💰 [DEBT UPDATE] Müşteri adı değişti, debt/current güncelleniyor...');
        
        const debtCurrentPath = `users/${userEmail}/customerList/${selectedCustomer.id}/debt/current`;
        console.log('   📍 Debt path:', debtCurrentPath);
        
        const debtCurrentRef = doc(db, debtCurrentPath);
        
        try {
          const debtCurrentDoc = await getDoc(debtCurrentRef);
          console.log('   📍 Debt document exists?:', debtCurrentDoc.exists());
          
          if (debtCurrentDoc.exists()) {
            const currentData = debtCurrentDoc.data();
            console.log('   📍 Mevcut customerName:', currentData.customerName);
            
            batch.update(debtCurrentRef, {
              customerName: editName.trim(),
              updatedAt: new Date().toISOString(),
              updatedBy: userEmail,
              lastNameUpdate: `Müşteri adı güncellendi: '${selectedCustomer['Müşteri Adı']}' → '${editName.trim()}'`
            });
            
            console.log('   ✅ [DEBT UPDATE] debt/current güncelleme batch\'e eklendi!');
          } else {
            console.log('   ℹ️ [DEBT UPDATE] debt/current dökümanı yok');
          }
        } catch (error) {
          console.error('   ❌ [DEBT UPDATE] Hata:', error);
        }
      } else {
        console.log('ℹ️ [DEBT UPDATE] Güncelleme gerekmiyor');
      }

      // Şantiyeleri güncelle (eğer ana müşteriyse)
      if (selectedCustomer.cariCode !== editCariCode.trim()) {
        const santiyeQuery = query(
          collection(db, COLLECTION_PATHS.USER_CUSTOMERS(userEmail)),
          where('parentId', '==', selectedCustomer.id)
        );
        const santiyeSnapshot = await getDocs(santiyeQuery);

        santiyeSnapshot.forEach((santiyeDoc) => {
          const santiyeData = santiyeDoc.data();
          const oldSantiyeCariCode = santiyeData['Şantiye Cari Kodu'];
          const santiyeNumber = oldSantiyeCariCode?.split('/')?.pop() || '';
          const newSantiyeCariCode = santiyeNumber ? `${editCariCode.trim()}/${santiyeNumber}` : editCariCode.trim();

          batch.update(santiyeDoc.ref, {
            'Şantiye Cari Kodu': newSantiyeCariCode,
            'cariCode': newSantiyeCariCode,
            'Onay': CUSTOMER_STATUS.APPROVED
          });

          // Şantiyelerin puantajlarını da güncelle - timeSheets koleksiyonunu kullan
          const santiyePuantajQuery = query(
            timeSheetsRef,
            where('Müşteri Adı', '==', santiyeData['Müşteri Adı'])
          );
          getDocs(santiyePuantajQuery).then((santiyePuantajSnapshot) => {
            santiyePuantajSnapshot.forEach((puantajDoc) => {
              batch.update(puantajDoc.ref, {
                'Müşteri Adı': santiyeData['Müşteri Adı']
              });
            });
          });
        });
      }

      await batch.commit();
      
      // Başarı mesajı - müşteri adı değiştiyse özel mesaj
      const successMsg = selectedCustomer['Müşteri Adı'] !== editName.trim()
        ? 'Müşteri adı, puantajlar, cari kasa (debt/current) ve şantiyeler başarıyla güncellendi.'
        : 'Müşteri ve ilgili veriler başarıyla güncellendi.';
      
      setSuccessMessage(successMsg);
      setAlertOpen(true);
      onClose();
    } catch (error) {
      console.error('Güncelleme sırasında hata:', error);
      setError('Güncelleme sırasında bir hata oluştu.');
      setAlertOpen(true);
    }
  };

  const confirmUpdate = async () => {
    setIsConfirmUpdateOpen(false);
    const db = getFirestore();
    const batch = writeBatch(db);

    try {
      // Eğer şantiye ise ve şantiye adı varsa kullan, yoksa müşteri adını kullan
      const oldSantiyeName = selectedCustomer 
        ? normalizeString(selectedCustomer['Müşteri Adı']) 
        : normalizeString(selectedCustomer['Müşteri Adı']);

      // Ana müşteriyi güncelle
      const customerRef = doc(db, `users/${auth.currentUser?.email}/customerList`, selectedCustomer.id);
      batch.update(customerRef, {
        'Müşteri Adı': editName.trim(),
        Telefon: editPhone.trim(),
        'E-posta': editEmail.trim(),
        cariCode: editCariCode.trim(),
        Onay: 'Onaylandı',
        ...(specialHourlyRate !== '' ? { 'Özel Saatlik Fiyat': Number(specialHourlyRate) } : { 'Özel Saatlik Fiyat': null }),
        ...(specialIcSeferFiyati !== '' ? { 'Özel İç Sefer Fiyatı': Number(specialIcSeferFiyati) } : { 'Özel İç Sefer Fiyatı': null }),
        ...(specialDisSeferFiyati !== '' ? { 'Özel Dış Sefer Fiyatı': Number(specialDisSeferFiyati) } : { 'Özel Dış Sefer Fiyatı': null }),
        'vergiNumarasi': editTaxNumber.replace(/\D/g, '').trim() // Vergi numarası (kurumsal müşteri eşleşmesi için)
      });

      // İlgili Puantajları Güncelle
      const puantajlarRef = collection(db, 'puantajlar');
      const puantajQuery = query(
        puantajlarRef,
        where('Müşteri Adı', '==', oldSantiyeName)
      );
      const puantajlarSnapshot = await getDocs(puantajQuery);

      puantajlarSnapshot.forEach((puantajDoc) => {
        batch.update(puantajDoc.ref, {
          'Müşteri Adı': editName.trim(),
          'Cari Kodu': editCariCode.trim(),
        });
      });

      // ⭐ YENİ: Müşteri adı değiştiğinde debt/current subcollection'ı da güncelle
      console.log('🔍 [DEBT UPDATE] Kontrol başlıyor...');
      console.log('   📍 Eski ad:', oldSantiyeName);
      console.log('   📍 Yeni ad:', editName.trim());
      console.log('   📍 Müşteri ID:', selectedCustomer?.id);
      console.log('   📍 Ad değişti mi?:', oldSantiyeName !== editName.trim());
      
      if (oldSantiyeName !== editName.trim() && selectedCustomer?.id) {
        console.log('💰 [DEBT UPDATE] Müşteri adı değişti, debt/current güncelleniyor...');
        
        // Doğrudan debt/current path'ini oluştur
        const debtCurrentPath = `users/${auth.currentUser?.email}/customerList/${selectedCustomer.id}/debt/current`;
        console.log('   📍 Debt path:', debtCurrentPath);
        
        const debtCurrentRef = doc(db, debtCurrentPath);
        
        // Önce mevcut debt'i kontrol et
        try {
          const debtCurrentDoc = await getDoc(debtCurrentRef);
          console.log('   📍 Debt document exists?:', debtCurrentDoc.exists());
          
          if (debtCurrentDoc.exists()) {
            const currentData = debtCurrentDoc.data();
            console.log('   📍 Mevcut customerName:', currentData.customerName);
            console.log('   📍 Mevcut amount:', currentData.amount);
          }
        } catch (readError) {
          console.log('   ⚠️ Debt okuma hatası (devam ediyoruz):', readError);
        }
        
        // Batch'e debt güncellemeyi ekle - document yoksa bile çalışır
        const debtUpdateData = {
          customerName: editName.trim(),
          updatedAt: new Date().toISOString(),
          updatedBy: auth.currentUser?.email,
          lastNameUpdate: `Müşteri adı güncellendi: '${oldSantiyeName}' → '${editName.trim()}'`
        };
        
        console.log('   📝 Güncelleme data:', debtUpdateData);
        
        try {
          batch.update(debtCurrentRef, debtUpdateData);
          console.log('   ✅ [DEBT UPDATE] debt/current güncelleme batch\'e eklendi!');
        } catch (batchError) {
          console.error('   ❌ [DEBT UPDATE] Batch ekleme hatası:', batchError);
        }
      } else {
        if (oldSantiyeName === editName.trim()) {
          console.log('ℹ️ [DEBT UPDATE] Müşteri adı değişmedi, güncelleme gerekmiyor');
        }
        if (!selectedCustomer?.id) {
          console.error('❌ [DEBT UPDATE] Müşteri ID bulunamadı!', selectedCustomer);
        }
      }

      // Şantiyeleri güncelle (eğer ana müşteriyse)
      if (selectedCustomer.cariCode !== editCariCode.trim()) {
        const santiyeQuery = query(
          collection(db, `users/${auth.currentUser?.email}/customerList`),
          where('parentId', '==', selectedCustomer.id)
        );
        const santiyeSnapshot = await getDocs(santiyeQuery);

        santiyeSnapshot.forEach((santiyeDoc) => {
          const santiyeData = santiyeDoc.data();
          const oldSantiyeCariCode = santiyeData['Şantiye Cari Kodu'];
          const santiyeNumber = oldSantiyeCariCode?.split('/')?.pop() || '';
          const newSantiyeCariCode = santiyeNumber ? `${editCariCode.trim()}/${santiyeNumber}` : editCariCode.trim();

          batch.update(santiyeDoc.ref, {
            'Şantiye Cari Kodu': newSantiyeCariCode,
            cariCode: newSantiyeCariCode,
          });
        });

        // Şantiyelere ait puantajları güncelle
        for (const santiyeDoc of santiyeSnapshot.docs) {
          const santiyeData = santiyeDoc.data();
          const santiyePuantajQuery = query(
            puantajlarRef,
            where('Müşteri Adı', '==', santiyeData['Müşteri Adı'])
          );
          const santiyePuantajSnapshot = await getDocs(santiyePuantajQuery);

          santiyePuantajSnapshot.forEach((puantajDoc) => {
            const newSantiyeCariCode = `${editCariCode.trim()}/${santiyeData['Şantiye Cari Kodu']?.split('/')?.pop() || ''}`;
            batch.update(puantajDoc.ref, {
              'Cari Kodu': newSantiyeCariCode,
            });
          });
        }
      }

      // Tüm değişiklikleri commit et
      await batch.commit();

      // Başarı mesajı - müşteri adı değiştiyse özel mesaj
      const successMsg = oldSantiyeName !== editName.trim() 
        ? 'Müşteri adı, puantajlar, cari kasa (debt/current) ve şantiyeler başarıyla güncellendi.'
        : 'Müşteri ve ilgili şantiyeler başarıyla güncellendi.';
      
      setSuccessMessage(successMsg);
      setAlertOpen(true);
      onClose();
      setSelectedCustomer(null);
      setUpdateChanges([]);
    } catch (error) {
      console.error('Müşteri güncellenirken bir hata oluştu:', error);
      setError(`Müşteri güncellenirken bir hata oluştu: ${error.message}`);
      setAlertOpen(true);
    }
  };

  const handleConflictResolution = async () => {
    setIsConflictModalOpen(false);

    const db = getFirestore();
    try {
      if (!conflictingCustomer || !conflictingCustomer.id) {
        throw new Error('Çakışan müşteri bilgisi eksik veya geçersiz.');
      }

      console.log('🔄 Müşteri birleştirme işlemi başlıyor:', {
        çakışanMüşteri: conflictingCustomer['Müşteri Adı'],
        güncellenecekMüşteri: editName.trim(),
        çakışanId: conflictingCustomer.id,
        güncellenecekId: selectedCustomer.id
      });

      // Batch işlemi oluştur
      const batch = writeBatch(db);
      const userEmail = auth.currentUser?.email;

      // 1. Çakışan müşterinin puantajlarını yeni müşteri adıyla güncelle
      const timeSheetsRef = collection(db, `users/${userEmail}/timeSheets`);
      const conflictingPuantajQuery = query(
        timeSheetsRef,
        where('Müşteri Adı', '==', conflictingCustomer['Müşteri Adı'])
      );
      
      const conflictingPuantajSnapshot = await getDocs(conflictingPuantajQuery);
      console.log(`📊 Çakışan müşterinin puantaj sayısı: ${conflictingPuantajSnapshot.size}`);
      
      // Makine saatlik ücret bilgisini alma fonksiyonu
      const getMachineHourlyRate = async (machineName) => {
        try {
          const machinesRef = collection(db, `users/${userEmail}/machines`);
          const machineQuery = query(machinesRef, where('MakineAdı', '==', machineName));
          const machineSnapshot = await getDocs(machineQuery);
          
          if (!machineSnapshot.empty) {
            const machineData = machineSnapshot.docs[0].data();
            const hourlyRate = parseFloat(machineData.BirimFiyat || machineData['Birim Fiyat'] || '0');
            console.log(`🔧 Makine saatlik ücreti bulundu: ${machineName} = ${hourlyRate} TL/saat`);
            return hourlyRate;
          } else {
            console.warn(`⚠️ Makine bulunamadı: ${machineName}`);
            return 0;
          }
        } catch (error) {
          console.error('❌ Makine saatlik ücreti alınırken hata:', error);
          return 0;
        }
      };

      // İyileştirilmiş puantaj tutarı hesaplama fonksiyonu
      const calculatePuantajAmount = async (puantajData) => {
        console.log('💰 Puantaj tutar hesaplaması başlıyor:', puantajData);
        
        // Çalışma saati bilgisini farklı field adlarından al
        let workingTime = puantajData['Toplam Çalışma Süresi'] || 
                         puantajData.totalWorkingTime || 
                         puantajData['Total Working Time'] ||
                         puantajData['toplamCalisSuresi'] ||
                         puantajData['toplam_calista_suresi'] ||
                         '0:00';
        
        // Eğer toplam çalışma süresi yoksa, saat bilgilerinden hesapla
        if (!workingTime || workingTime === '0:00' || workingTime === '00:00') {
          console.log('📊 Toplam çalışma süresi bulunamadı, saat bilgilerinden hesaplanıyor...');
          
          let totalMinutes = 0;
          
          // 1. Vardiya
          const start1 = puantajData['1. Başlangıç Saati'] || puantajData['startTime1'] || puantajData['start1'];
          const end1 = puantajData['1. Bitiş Saati'] || puantajData['endTime1'] || puantajData['end1'];
          
          if (start1 && end1) {
            const duration1 = calculateTimeDuration(start1, end1);
            totalMinutes += duration1;
            console.log(`⏰ 1. Vardiya: ${start1} - ${end1} = ${duration1} dakika`);
          }
          
          // 2. Vardiya
          const start2 = puantajData['2. Başlangıç Saati'] || puantajData['startTime2'] || puantajData['start2'];
          const end2 = puantajData['2. Bitiş Saati'] || puantajData['endTime2'] || puantajData['end2'];
          
          if (start2 && end2) {
            const duration2 = calculateTimeDuration(start2, end2);
            totalMinutes += duration2;
            console.log(`⏰ 2. Vardiya: ${start2} - ${end2} = ${duration2} dakika`);
          }
          
          // 3. Vardiya (eğer varsa)
          const start3 = puantajData['3. Başlangıç Saati'] || puantajData['startTime3'] || puantajData['start3'];
          const end3 = puantajData['3. Bitiş Saati'] || puantajData['endTime3'] || puantajData['end3'];
          
          if (start3 && end3) {
            const duration3 = calculateTimeDuration(start3, end3);
            totalMinutes += duration3;
            console.log(`⏰ 3. Vardiya: ${start3} - ${end3} = ${duration3} dakika`);
          }
          
          // Hesaplanan toplam süreyi HH:MM formatına çevir
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          workingTime = `${hours}:${minutes.toString().padStart(2, '0')}`;
          console.log(`📊 Hesaplanan toplam çalışma süresi: ${workingTime} (${totalMinutes} dakika)`);
        }
        
        // Saatlik ücret bilgisini farklı field adlarından al
        let hourlyRate = parseFloat(
          puantajData['Saatlik Ücret'] || 
          puantajData.hourlyRate || 
          puantajData['Hourly Rate'] ||
          puantajData['saatlikUcret'] ||
          puantajData['saatlik_ucret'] ||
          puantajData['BirimFiyat'] ||
          puantajData['birimFiyat'] ||
          puantajData['birim_fiyat'] ||
          '0'
        );
        
        // Eğer saatlik ücret bulunamazsa makine bilgilerinden çek
        if (hourlyRate === 0) {
          console.log('💰 Puantajda saatlik ücret bulunamadı, makine bilgilerinden çekiliyor...');
          const machineName = puantajData['Makine İsmi'] || puantajData['machineName'] || puantajData['Makine Adı'];
          if (machineName) {
            hourlyRate = await getMachineHourlyRate(machineName);
          }
        }
        
        console.log(`💰 Saatlik ücret: ${hourlyRate} TL`);
        
        // Çalışma saatini hesapla - iOS HopsPlus ile AYNI MANTIK
        const totalMinutes = parseWorkingTime(workingTime);
        const totalHours = totalMinutes / 60;
        
        const totalAmount = totalHours * hourlyRate;
        
        console.log(`📊 Tutar hesaplama sonucu: ${totalHours} saat × ${hourlyRate} TL = ${totalAmount} TL`);
        
        return totalAmount;
      };
      
      // Saat aralığından dakika hesaplama yardımcı fonksiyonu
      const calculateTimeDuration = (startTime, endTime) => {
        if (!startTime || !endTime) return 0;
        
        const parseTime = (timeStr) => {
          const [hours, minutes] = timeStr.split(':').map(Number);
          if (isNaN(hours) || isNaN(minutes)) return null;
          return hours * 60 + minutes;
        };
        
        const startMinutes = parseTime(startTime);
        const endMinutes = parseTime(endTime);
        
        if (startMinutes === null || endMinutes === null) return 0;
        
        // Gece yarısı geçişi kontrolü
        let duration = endMinutes - startMinutes;
        if (duration < 0) {
          duration += 24 * 60; // Ertesi güne geçiş
        }
        
        return duration;
      };

      // Aktarılan puantajların detaylarını topla (cari geçmişi için)
      const transferredPuantajlar = [];
      
      // Çakışan müşterinin tüm puantajlarını yeni müşteri adıyla güncelle
      for (const puantajDoc of conflictingPuantajSnapshot.docs) {
        const puantajData = puantajDoc.data();
        
        // Puantaj detaylarını kaydet (cari geçmişi için) - async olarak hesapla
        const totalAmount = await calculatePuantajAmount(puantajData) || 0;
        
        transferredPuantajlar.push({
          id: puantajDoc.id,
          puantajCount: puantajData['Puantaj No'] || puantajData.puantajCount || 'Bilinmeyen',
          machineName: puantajData['Makine İsmi'] || puantajData.machineName || 'Bilinmeyen',
          totalWorkingTime: puantajData['Toplam Çalışma Süresi'] || puantajData.totalWorkingTime || '0:00',
          date: puantajData['Tarih'] || puantajData.date || new Date().toISOString().split('T')[0],
          hourlyRate: puantajData['Saatlik Ücret'] || puantajData.hourlyRate || 0,
          totalAmount: totalAmount
        });
        
        batch.update(puantajDoc.ref, {
          'Müşteri Adı': editName.trim(),
          'Cari Kodu': editCariCode.trim(),
          'mergedFrom': conflictingCustomer['Müşteri Adı'], // Hangi müşteriden birleştirildiği
          'mergedAt': new Date().toISOString(),
          'mergedBy': userEmail,
          'mergedReason': `🔄 Müşteri birleştirme: "${conflictingCustomer['Müşteri Adı']}" → "${editName.trim()}"`,
          'originalCustomerName': conflictingCustomer['Müşteri Adı'] // Orijinal müşteri adını sakla
        });
      }

      // 2. Ana müşterinin puantajlarını işle (sadece müşteri adı değişiyorsa cari geçmişine ekle)
      let shouldAddMainCustomerPuantajToHistory = false;
      
      if (selectedCustomer['Müşteri Adı'] !== editName.trim()) {
        // Müşteri adı değişiyor - puantajları güncelle VE cari geçmişine ekle
        shouldAddMainCustomerPuantajToHistory = true;
        
        const oldPuantajQuery = query(
          timeSheetsRef,
          where('Müşteri Adı', '==', selectedCustomer['Müşteri Adı'])
        );
        
        const oldPuantajSnapshot = await getDocs(oldPuantajQuery);
        console.log(`📊 Ana müşteri puantajları güncelleniyor: ${oldPuantajSnapshot.size}`);
        
        // Ana müşterinin puantajlarını güncelle ve cari geçmişi için kaydet
        for (const puantajDoc of oldPuantajSnapshot.docs) {
          const puantajData = puantajDoc.data();
          
          // Ana müşterinin puantaj detaylarını kaydet (cari geçmişi için) - async olarak hesapla
          const totalAmount = await calculatePuantajAmount(puantajData) || 0;
          
          transferredPuantajlar.push({
            id: puantajDoc.id,
            puantajCount: puantajData['Puantaj No'] || puantajData.puantajCount || 'Bilinmeyen',
            machineName: puantajData['Makine İsmi'] || puantajData.machineName || 'Bilinmeyen',
            totalWorkingTime: puantajData['Toplam Çalışma Süresi'] || puantajData.totalWorkingTime || '0:00',
            date: puantajData['Tarih'] || puantajData.date || new Date().toISOString().split('T')[0],
            hourlyRate: puantajData['Saatlik Ücret'] || puantajData.hourlyRate || 0,
            totalAmount: totalAmount,
            isMainCustomerPuantaj: true // Ana müşterinin puantajı olduğunu işaretle
          });
          
          batch.update(puantajDoc.ref, {
            'Müşteri Adı': editName.trim(),
            'Cari Kodu': editCariCode.trim(),
            'updatedAt': new Date().toISOString(),
            'updatedBy': userEmail,
            'nameUpdated': `Müşteri adı güncellendi: "${selectedCustomer['Müşteri Adı']}" → "${editName.trim()}"`,
            'originalCustomerName': selectedCustomer['Müşteri Adı']
          });
        }
      } else {
        // Müşteri adı değişmiyor (A → A birleştirme) - sadece puantajları güncelle, cari geçmişine EKLEME
        console.log(`📊 Aynı müşteri adına birleştirme: "${selectedCustomer['Müşteri Adı']}" - mevcut puantajlar cari geçmişine eklenmeyecek`);
        shouldAddMainCustomerPuantajToHistory = false;
      }

      // 3. CARİ KASA BORÇ BİRLEŞTİRME İŞLEMİ
      console.log('💰 Cari kasa borç birleştirme işlemi başlıyor...');
      
      // Çakışan müşterinin borç bilgilerini al
      const conflictingDebtRef = doc(db, `users/${userEmail}/customerList/${conflictingCustomer.id}/debt/current`);
      const conflictingDebtDoc = await getDoc(conflictingDebtRef);
      
      // Ana müşterinin borç bilgilerini al
      const mainDebtRef = doc(db, `users/${userEmail}/customerList/${selectedCustomer.id}/debt/current`);
      const mainDebtDoc = await getDoc(mainDebtRef);
      
      let conflictingDebtData = null;
      let mainDebtData = null;
      
      if (conflictingDebtDoc.exists()) {
        conflictingDebtData = conflictingDebtDoc.data();
      }
      
      if (mainDebtDoc.exists()) {
        mainDebtData = mainDebtDoc.data();
      }
      
      // Borç birleştirme hesaplamaları
      const conflictingAmount = conflictingDebtData?.amount || 0;
      const conflictingHistory = conflictingDebtData?.history || [];
      const mainAmount = mainDebtData?.amount || 0;
      const mainHistory = mainDebtData?.history || [];
      
      console.log(`💰 Çakışan müşteri borcu: ${conflictingAmount} TL (${conflictingHistory.length} işlem)`);
      console.log(`💰 Ana müşteri borcu: ${mainAmount} TL (${mainHistory.length} işlem)`);
      
      // Yeni toplam borç miktarı
      const newTotalAmount = mainAmount + conflictingAmount;
      
      // Çakışan müşterinin history'sini transfer olarak işaretle
      const transferredHistory = conflictingHistory.map(historyItem => {
        const originalName = conflictingCustomer['Müşteri Adı'];
        const finalName = editName.trim();

        // Eğer orijinal isim final isimle aynıysa transfer flagi ekleme
        if (originalName === finalName) {
          return {
            ...historyItem,
            transferredAt: new Date().toISOString(),
            transferredBy: userEmail
          };
        }

        // Farklıysa transfer bilgilerini ekle
        return {
          ...historyItem,
          transferredFrom: originalName,
          transferredTo: finalName,
          transferredAt: new Date().toISOString(),
          transferredBy: userEmail
        };
      });
      
      // Aktarılan puantajlar için cari geçmişi kayıtları oluştur
      const finalName = editName.trim();
      // Her iki müşterinin puantajını işle, sadece final isimle aynı olmayanlar aktarım olarak flaglenir
      const puantajHistoryEntries = transferredPuantajlar.map(puantaj => {
        const puantajCustomerName = puantaj.isMainCustomerPuantaj
          ? selectedCustomer['Müşteri Adı']
          : conflictingCustomer['Müşteri Adı'];
        const isTransfer = puantajCustomerName !== finalName;

        // Tarihi formatla (DD.MM.YYYY -> ISO)
        let puantajDate = puantaj.date;
        if (puantaj.date && puantaj.date.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
          const [day, month, year] = puantaj.date.split('.');
          puantajDate = new Date(`${year}-${month}-${day}`).toISOString();
        } else if (puantaj.date && !puantaj.date.includes('T')) {
          puantajDate = new Date(puantaj.date + 'T12:00:00.000Z').toISOString();
        }

        return {
          id: `puantaj_${isTransfer ? 'transfer' : 'mevcut'}_${puantaj.id}_${Date.now()}`,
          amount: puantaj.totalAmount,
          date: puantajDate,
          description: isTransfer
            ? `📊 PUANTAJ AKTARIM: ${puantaj.puantajCount} - ${puantaj.machineName} (${puantaj.totalWorkingTime}) [${puantaj.totalAmount.toLocaleString('tr-TR')} TL] | "${puantajCustomerName}" → "${finalName}"`
            : `📊 PUANTAJ (MEVCUT): ${puantaj.puantajCount} - ${puantaj.machineName} (${puantaj.totalWorkingTime}) [${puantaj.totalAmount.toLocaleString('tr-TR')} TL] | "${puantajCustomerName}" müşterisinin mevcut puantajı`,
          type: 'increase',
          ...(isTransfer ? { transferFrom: puantajCustomerName, transferredFrom: puantajCustomerName } : {}),
          transferTo: finalName,
          transferredAt: new Date().toISOString(),
          transferredBy: userEmail,
          puantajCode: puantaj.puantajCount,
          machineName: puantaj.machineName,
          machineHours: puantaj.totalWorkingTime,
          hourlyRate: puantaj.hourlyRate,
          originalPuantajId: puantaj.id,
          transferType: isTransfer ? 'puantaj_merge' : 'puantaj_existing',
          mergeOperation: true,
          isMainCustomerPuantaj: puantaj.isMainCustomerPuantaj || false
        };
      });
      
      // Transfer bilgi kaydı oluştur (eğer çakışan müşterinin borcu varsa)
      let transferInfoEntry = null;
      if (conflictingAmount > 0 || conflictingHistory.length > 0) {
        transferInfoEntry = {
          id: `merge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          amount: 0, // Sıfır - ekstra borç yaratmaz, sadece bilgi amaçlı
          date: new Date().toISOString(),
                     description: `🔄 MÜŞTERİ BİRLEŞTİRME: "${conflictingCustomer['Müşteri Adı']}" müşterisinden "${editName.trim()}" müşterisine aktarım (Toplam: ${conflictingAmount} TL, İşlem: ${conflictingHistory.length} adet)`,
          type: 'transfer_in',
          transferFrom: conflictingCustomer['Müşteri Adı'],
          transferTo: editName.trim(),
          isInfoOnly: true, // Sadece bilgi amaçlı işaret
          mergeOperation: true
        };
      }
      
      // Ana müşterinin yeni history'sini oluştur (tarih sıralaması ile)
      const newMainHistory = [
        ...mainHistory,
        ...(transferInfoEntry ? [transferInfoEntry] : []), // Transfer bilgi kaydı
        ...transferredHistory, // Çakışan müşterinin gerçek işlemleri
        ...puantajHistoryEntries // Aktarılan puantajların detayları (aynı müşteri adına birleştirmede sadece çakışan müşteri puantajları)
      ];

      // 🐞 DEBUG: Duplikasyon kontrolü ve detaylı loglar
      const allHistoryPuantajCodes = newMainHistory
        .filter(h => h.puantajCode !== undefined && h.puantajCode !== null)
        .map(h => h.puantajCode);
      const uniqueHistoryPuantajCodes = [...new Set(allHistoryPuantajCodes)];
      console.log('🐞 DEBUG | Toplam puantajlı history kaydı:', allHistoryPuantajCodes.length);
      console.log('🐞 DEBUG | Benzersiz puantajCode sayısı:', uniqueHistoryPuantajCodes.length);
      if (allHistoryPuantajCodes.length !== uniqueHistoryPuantajCodes.length) {
        console.warn('🚨 DUPLICATE PUANTAJ HISTORY ENTRIES TESPİT EDİLDİ!', {
          allCodes: allHistoryPuantajCodes,
          uniqueCodes: uniqueHistoryPuantajCodes,
        });
      }

      // 🐞 DEBUG: Borç tutarı doğrulama
      const historyTotalFromEntries = newMainHistory
        .filter(h => typeof h.amount === 'number')
        .reduce((sum, h) => sum + h.amount, 0);
      console.log('🐞 DEBUG | History toplam tutar (entry sum):', historyTotalFromEntries);
      console.log('🐞 DEBUG | Beklenen yeni toplam borç (mainAmount + conflictingAmount):', newTotalAmount);

      // Ana müşterinin borç bilgilerini güncelle
      const mainDebtUpdateData = {
        amount: newTotalAmount,
        history: newMainHistory,
        lastUpdated: new Date().toISOString(),
        customerName: editName.trim(),
        mergedFrom: conflictingCustomer['Müşteri Adı'],
        mergedAt: new Date().toISOString()
      };
      
      // Batch'e ana müşterinin borç güncellemesini ekle
      batch.set(mainDebtRef, mainDebtUpdateData);
      
      // Çakışan müşterinin borç bilgilerini temizle/sıfırla
      if (conflictingDebtDoc.exists()) {
        const conflictingDebtUpdateData = {
          amount: 0,
          history: [],
          lastUpdated: new Date().toISOString(),
          customerName: conflictingCustomer['Müşteri Adı'],
          transferredTo: editName.trim(),
          transferredAt: new Date().toISOString(),
          transferredBy: userEmail
        };
        
        batch.set(conflictingDebtRef, conflictingDebtUpdateData);
      }
      
      console.log(`💰 Cari kasa birleştirme hazırlandı: ${conflictingAmount} TL → ${newTotalAmount} TL`);

      // 4. Çakışan müşteriyi sil
      const conflictingCustomerRef = doc(db, `users/${userEmail}/customerList`, conflictingCustomer.id);
      batch.delete(conflictingCustomerRef);

      // 5. Ana müşteriyi güncelle
      const customerRef = doc(db, `users/${userEmail}/customerList`, selectedCustomer.id);
      const updateData = {
        'Müşteri Adı': editName.trim(),
        'Telefon': editPhone.trim(),
        'E-posta': editEmail.trim(),
        'cariCode': editCariCode.trim(),
        'Onay': 'Onaylandı',
        'updatedAt': new Date().toISOString(),
        'updatedBy': userEmail,
        'mergedWith': conflictingCustomer['Müşteri Adı'] // Hangi müşteri ile birleştirildiği
      };
      batch.update(customerRef, updateData);

      // 6. Şantiyeleri güncelle (eğer ana müşteriyse)
      if (selectedCustomer.cariCode !== editCariCode.trim()) {
        const santiyeQuery = query(
          collection(db, `users/${userEmail}/customerList`),
          where('parentId', '==', selectedCustomer.id)
        );
        const santiyeSnapshot = await getDocs(santiyeQuery);

        santiyeSnapshot.forEach((santiyeDoc) => {
          const santiyeData = santiyeDoc.data();
          const oldSantiyeCariCode = santiyeData['Şantiye Cari Kodu'];
          const santiyeNumber = oldSantiyeCariCode?.split('/')?.pop() || '';
          const newSantiyeCariCode = santiyeNumber ? `${editCariCode.trim()}/${santiyeNumber}` : editCariCode.trim();

          batch.update(santiyeDoc.ref, {
            'Şantiye Cari Kodu': newSantiyeCariCode,
            'cariCode': newSantiyeCariCode,
            'Onay': 'Onaylandı',
            'updatedAt': new Date().toISOString(),
            'updatedBy': userEmail
          });
        });
      }

      // 7. Batch işlemini commit et
      await batch.commit();

      console.log('✅ Müşteri birleştirme işlemi tamamlandı:', {
        birleştirilenPuantajSayısı: conflictingPuantajSnapshot.size,
        güncellenecekMüşteri: editName.trim(),
        çakışanMüşteriSilindi: conflictingCustomer['Müşteri Adı'],
        birleştirilenBorç: conflictingAmount,
        yeniToplamBorç: newTotalAmount,
        birleştirilenİşlemSayısı: conflictingHistory.length
      });

      // Detaylı başarı mesajı oluştur
      let detailedMessage = `🎉 MÜŞTERİ BİRLEŞTİRME TAMAMLANDI!\n\n`;
      
      // Müşteri bilgileri
      detailedMessage += `📋 SONUÇ:\n`;
      detailedMessage += `• "${conflictingCustomer['Müşteri Adı']}" müşterisi silindi\n`;
      detailedMessage += `• Tüm veriler "${editName.trim()}" müşterisine aktarıldı\n\n`;
      
      // Puantaj bilgileri
      if (transferredPuantajlar.length > 0) {
        // Puantaj tutarlarını yeniden hesapla (0 TL sorununu çöz)
        const recalculatedPuantajlar = transferredPuantajlar.map(p => {
          let amount = p.totalAmount;
          if (!amount || amount === 0) {
            const workingTime = p.totalWorkingTime || '0:00';
            const hourlyRate = parseFloat(p.hourlyRate || '0');
            // iOS HopsPlus ile AYNI MANTIK
            const totalMinutes = parseWorkingTime(workingTime);
            const totalHours = totalMinutes / 60;
            amount = totalHours * hourlyRate;
          }
          return { ...p, totalAmount: amount };
        });
        
        const totalPuantajAmount = recalculatedPuantajlar.reduce((sum, p) => sum + p.totalAmount, 0);
        const conflictingPuantajCount = recalculatedPuantajlar.filter(p => !p.isMainCustomerPuantaj).length;
        const mainPuantajCount = recalculatedPuantajlar.filter(p => p.isMainCustomerPuantaj).length;
        
        detailedMessage += `📊 PUANTAJ BİRLEŞTİRME:\n`;
        detailedMessage += `• "${conflictingCustomer['Müşteri Adı']}" müşterisinden ${conflictingPuantajCount} adet puantaj aktarıldı\n`;
        
        if (shouldAddMainCustomerPuantajToHistory && mainPuantajCount > 0) {
          detailedMessage += `• "${selectedCustomer['Müşteri Adı']}" müşterisinin ${mainPuantajCount} adet mevcut puantajı cari geçmişine eklendi\n`;
          detailedMessage += `• TOPLAM: ${recalculatedPuantajlar.length} adet puantaj cari geçmişinde görünecek\n`;
        } else {
          detailedMessage += `• TOPLAM: ${conflictingPuantajCount} adet puantaj cari geçmişinde görünecek\n`;
          if (!shouldAddMainCustomerPuantajToHistory) {
            detailedMessage += `• "${selectedCustomer['Müşteri Adı']}" mevcut puantajları zaten mevcut (aynı müşteri adı)\n`;
          }
        }
        
        detailedMessage += `• Toplam puantaj tutarı: ${totalPuantajAmount.toLocaleString('tr-TR')} TL\n`;
        detailedMessage += `• Final müşteri: "${editName.trim()}"\n`;
        detailedMessage += `• Her puantaj cari kasa geçmişinde ayrı ayrı görünecek\n\n`;
        
        // Puantajları kategorilere ayırarak listele
        const conflictingPuantajlar = recalculatedPuantajlar.filter(p => !p.isMainCustomerPuantaj);
        const mainPuantajlar = recalculatedPuantajlar.filter(p => p.isMainCustomerPuantaj);
        
        if (conflictingPuantajlar.length > 0) {
          detailedMessage += `📋 AKTARILAN PUANTAJLAR (${conflictingCustomer['Müşteri Adı']}):\n`;
          conflictingPuantajlar.forEach((puantaj, index) => {
            detailedMessage += `  ${index + 1}. ${puantaj.puantajCount} - ${puantaj.machineName} (${puantaj.totalWorkingTime}) - ${puantaj.totalAmount.toLocaleString('tr-TR')} TL\n`;
          });
          detailedMessage += `\n`;
        }
        
        if (mainPuantajlar.length > 0 && shouldAddMainCustomerPuantajToHistory) {
          detailedMessage += `📋 MEVCUT PUANTAJLAR CARİ GEÇMİŞİNE EKLENDİ (${selectedCustomer['Müşteri Adı']}):\n`;
          mainPuantajlar.forEach((puantaj, index) => {
            detailedMessage += `  ${index + 1}. ${puantaj.puantajCount} - ${puantaj.machineName} (${puantaj.totalWorkingTime}) - ${puantaj.totalAmount.toLocaleString('tr-TR')} TL\n`;
          });
          detailedMessage += `\n`;
        }
      } else {
        detailedMessage += `📊 PUANTAJ: Birleştirilecek puantaj bulunamadı\n\n`;
      }
      
      // Cari kasa bilgileri
      if (conflictingAmount > 0 || conflictingHistory.length > 0) {
        detailedMessage += `💰 CARİ KASA AKTARİMİ:\n`;
        if (conflictingAmount > 0) {
          detailedMessage += `• ${conflictingAmount.toLocaleString('tr-TR')} TL borç aktarıldı\n`;
          detailedMessage += `• Yeni toplam borç: ${newTotalAmount.toLocaleString('tr-TR')} TL\n`;
        }
        if (conflictingHistory.length > 0) {
          detailedMessage += `• ${conflictingHistory.length} adet işlem geçmişi aktarıldı\n`;
          detailedMessage += `• Cari kasa geçmişinde "transfer" olarak görünecek\n`;
        }
        detailedMessage += `• Kaynak: "${conflictingCustomer['Müşteri Adı']}" müşterisi\n`;
        detailedMessage += `• Hedef: "${editName.trim()}" müşterisi\n\n`;
      } else {
        detailedMessage += `💰 CARİ KASA: Aktarılacak borç bulunamadı\n\n`;
      }
      
      // Kontrol etme talimatları
      detailedMessage += `🔍 KONTROL EDİN:\n`;
      detailedMessage += `• Puantajlar sayfasında "${editName.trim()}" müşterisinin puantajlarını kontrol edin\n`;
      detailedMessage += `• Cari Kasa sayfasında "${editName.trim()}" müşterisinin borç durumunu kontrol edin\n`;
      detailedMessage += `• Transfer edilen işlemler geçmişte "aktarıldı" etiketiyle görünecek`;

      setSuccessMessage(detailedMessage);
      setAlertOpen(true);
      onClose();
    } catch (error) {
      console.error('❌ Müşteri birleştirme hatası:', error);
      setError(`Müşteri birleştirme sırasında hata: ${error.message}`);
      setAlertOpen(true);
    }
  };

  const cancelUpdate = () => {
    setIsConfirmUpdateOpen(false);
    setUpdateChanges([]);
  };

  const handleSelectCustomer = (customer) => {
    setEditName(toTurkishUpperCase(customer['Müşteri Adı']));
    setEditPhone(customer['Telefon'] || '');
    setEditEmail(customer['E-posta'] || '');
    setEditCariCode(customer.cariCode);
    setEditTaxNumber(customer.vergiNumarasi || '');
    setIsCustomerSelectionOpen(false);
  };

  const handleWarningModalClose = () => {
    setIsWarningModalOpen(false);
  };

  const handleCariCodeSubmit = async () => {
    if (!editCariCode.trim()) {
      setError('Cari kodu gereklidir.');
      setAlertOpen(true);
      return;
    }

    setIsWarningModalOpen(false);
    await checkConflictsAndUpdate();
  };

  return (
    <>
      <Modal open={isOpen} onClose={handleCloseModal}>
        <ModalBox>
          <Typography variant="h6" gutterBottom>
            {`Müşteri Düzenle${selectedCustomer && selectedCustomer['Müşteri Adı'] ? ' - ' + selectedCustomer['Müşteri Adı'] : ''}${selectedCustomer && selectedCustomer.cariCode ? '/' + selectedCustomer.cariCode : ''}`}
          </Typography>
          <Box display="flex" alignItems="center">
            <TextField
              label="Müşteri Adı"
              fullWidth
              margin="normal"
              value={editName}
              onChange={(e) => setEditName(toTurkishUpperCase(e.target.value))}
            />
            {nameConflict === 'empty' && (
              <div style={{ color: 'red', fontSize: 13, marginLeft: 10 }}>
                Bu alan boş bırakılamaz!
              </div>
            )}
            {nameConflict === 'conflict' && conflictingNameCustomer && (
              <div style={{ color: 'red', fontSize: 13, marginLeft: 10 }}>
                Bu ad {conflictingNameCustomer['Müşteri Adı']} - {conflictingNameCustomer.cariCode} müşterisinde mevcut!
              </div>
            )}
            <StyledIconButton
              onClick={() => setIsCustomerSelectionOpen(!isCustomerSelectionOpen)}
              size="small"
            >
              <ArrowDropDownIcon fontSize="small" />
            </StyledIconButton>
          </Box>
          {isCustomerSelectionOpen && (
            <CustomerSelectionTable
              customers={approvedCustomers}
              onSelectCustomer={handleSelectCustomer}
              enableAutoUppercase={true}
            />
          )}
          <TextField
            label="Telefon"
            fullWidth
            margin="normal"
            value={editPhone}
            onChange={(e) => setEditPhone(e.target.value)}
          />
          <TextField
            label="E-posta"
            fullWidth
            margin="normal"
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
          />
          <TextField
            label="Vergi Numarası (Kurumsal Müşteriler İçin)"
            fullWidth
            margin="normal"
            value={editTaxNumber}
            onChange={(e) => setEditTaxNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
            inputProps={{ maxLength: 11 }}
            error={editTaxNumber.length > 0 && editTaxNumber.length !== 10 && editTaxNumber.length !== 11}
            helperText={
              editTaxNumber.length > 0 && editTaxNumber.length !== 10 && editTaxNumber.length !== 11
                ? `Vergi numarası 10 veya 11 haneli olmalıdır (${editTaxNumber.length} hane girildi)`
                : "10 veya 11 haneli vergi numarası (opsiyonel). Kurumsal müşterilerin puantaj görüntülemesi için gereklidir."
            }
            sx={{
              '& .MuiFormHelperText-root.Mui-error': {
                color: '#d32f2f',
                fontWeight: 'bold'
              }
            }}
          />
          <TextField
            label="Cari Kodu"
            fullWidth
            margin="normal"
            value={editCariCode}
            onChange={(e) => setEditCariCode(e.target.value)}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: '#666', fontWeight: 600 }}>
            Özel Fiyatlandırma
          </Typography>
          <TextField
            label="Özel Saatlik Fiyat (TL/saat) - Normal Makineler"
            fullWidth
            margin="normal"
            type="number"
            value={specialHourlyRate}
            onChange={(e) => setSpecialHourlyRate(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            helperText="Normal makineler için özel saatlik fiyat"
          />
          <Box display="flex" gap={2}>
            <TextField
              label="Özel İç Sefer Fiyatı (TL)"
              fullWidth
              margin="normal"
              type="number"
              value={specialIcSeferFiyati}
              onChange={(e) => setSpecialIcSeferFiyati(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              helperText="Kamyon iç sefer"
            />
            <TextField
              label="Özel Dış Sefer Fiyatı (TL)"
              fullWidth
              margin="normal"
              type="number"
              value={specialDisSeferFiyati}
              onChange={(e) => setSpecialDisSeferFiyati(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              helperText="Kamyon dış sefer"
            />
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#1976d2' }}>
            Özel fiyat tanımlanırsa makine fiyatı yerine bu fiyat kullanılır.
          </Typography>
          {codeConflict === 'empty' && (
            <div style={{ color: 'red', fontSize: 13, marginTop: 4 }}>
              Bu alan boş bırakılamaz!
            </div>
          )}
          {codeConflict === 'conflict' && conflictingCodeCustomer && (
            <div style={{ color: 'red', fontSize: 13, marginTop: 4 }}>
              Bu cari kodu {conflictingCodeCustomer['Müşteri Adı']} - {conflictingCodeCustomer.cariCode} müşterisinde mevcut!
            </div>
          )}
          {/* Formun en altına, ad veya cari kod çakışıyorsa genel bir uyarı ekle */}
          {(nameConflict === 'conflict' || codeConflict === 'conflict') && (
            <div style={{ color: 'red', fontWeight: 500, marginTop: 16, fontSize: 15 }}>
              Bu müşteri adı veya cari kodu başka bir müşteride mevcut. Dilerseniz bu müşterinin verisini aktarım yoluyla iletebilirsiniz.
            </div>
          )}
          <Box mt={2} display="flex" flexDirection="column" alignItems="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateCustomer}
              disabled={
                nameConflict === 'empty' || 
                nameConflict === 'conflict' || 
                codeConflict === 'empty' || 
                codeConflict === 'conflict' ||
                (editTaxNumber.length > 0 && editTaxNumber.length !== 10 && editTaxNumber.length !== 11)
              }
            >
              Güncelle
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              style={{ marginTop: 8 }}
              onClick={() => setIsTransferModalOpen(true)}
            >
              Veri Aktarma
            </Button>
          </Box>

          {/* 1. Şantiyeler başlığı ve tablosunu kaldır */}

          <ConfirmUpdateModal
            isOpen={isConfirmUpdateOpen}
            onClose={cancelUpdate}
            updateChanges={updateChanges}
            confirmUpdate={confirmUpdate}
            cancelUpdate={cancelUpdate}
          />

          <ConflictResolutionModal
            isOpen={isConflictModalOpen}
            onClose={() => setIsConflictModalOpen(false)}
            onConfirm={handleConflictResolution}
            conflictingCustomer={conflictingCustomer}
            newCustomerData={{
              'Müşteri Adı': editName,
              'cariCode': editCariCode,
            }}
            oldCustomerData={oldCustomerData}
          />

          {/* AddAsSantiyeModal kaldırıldı */}

          <Modal open={isWarningModalOpen} onClose={handleWarningModalClose}>
            <ModalBox>
              <Typography variant="h6" gutterBottom>
                Uyarı: Cari Kodu Eksik
              </Typography>
              <Typography variant="body1" gutterBottom>
                Cari kodu eklemediniz. Lütfen ekleyin.
              </Typography>
              <Box display="flex" alignItems="center" mt={2}>
                <Typography variant="body2" style={{ marginRight: '10px', flexGrow: 1 }}>
                  {editName} - {editPhone}
                </Typography>
                <TextField
                  label="Cari Kodu"
                  value={editCariCode}
                  onChange={(e) => setEditCariCode(e.target.value)}
                  style={{ width: '150px' }}
                />
              </Box>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button variant="contained" color="primary" onClick={handleCariCodeSubmit}>
                  Kaydet
                </Button>
              </Box>
            </ModalBox>
          </Modal>

          {/* 3. Popup/modal ekle */}
          <TransferCustomerModal
            isOpen={isTransferModalOpen}
            onClose={() => setIsTransferModalOpen(false)}
            selectedCustomer={selectedCustomer}
            setAlertOpen={setAlertOpen}
            setError={setError}
            setSuccessMessage={setSuccessMessage}
            editName={editName}
            editCariCode={editCariCode}
          />
        </ModalBox>
      </Modal>
      <Modal open={isConfirmChangeModalOpen} onClose={handleCancelChange}>
        <ModalBox>
          <Box display="flex" alignItems="center" mb={2}>
            <span style={{ fontSize: 32, color: '#e53935', marginRight: 12 }}>⚠️</span>
            <Typography variant="h6" fontWeight={700} color="#e53935">
              Kritik Değişiklik Onayı
            </Typography>
          </Box>
          <Typography variant="body1" gutterBottom>
            Müşteri adı veya cari kodunda değişiklik yapmak üzeresiniz. Bu işlem, ilgili tüm puantaj ve cari kasa kayıtlarını <b>kalıcı olarak</b> yeni müşteri adı/cari kodu ile güncelleyecek ve <b>geri alınamaz</b>.<br/><br/>
            Lütfen değişiklikleri dikkatlice kontrol edin ve emin değilseniz vazgeçin.
          </Typography>
          <Box mt={2} mb={2}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fafafa', borderRadius: 8 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: '#888' }}>Alan</th>
                  <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: '#888' }}>Eski Değer</th>
                  <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: '#888' }}>Yeni Değer</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: 8 }}>Müşteri Adı</td>
                  <td style={{ padding: 8, color: oldCustomerData?.['Müşteri Adı'] !== editName ? '#e53935' : '#333' }}>{oldCustomerData?.['Müşteri Adı']}</td>
                  <td style={{ padding: 8, color: oldCustomerData?.['Müşteri Adı'] !== editName ? '#43a047' : '#333' }}>{editName}</td>
                </tr>
                <tr>
                  <td style={{ padding: 8 }}>Cari Kod</td>
                  <td style={{ padding: 8, color: oldCustomerData?.['cariCode'] !== editCariCode ? '#e53935' : '#333' }}>{oldCustomerData?.['cariCode']}</td>
                  <td style={{ padding: 8, color: oldCustomerData?.['cariCode'] !== editCariCode ? '#43a047' : '#333' }}>{editCariCode}</td>
                </tr>
              </tbody>
            </table>
          </Box>
          <Typography variant="body2" color="#e53935" fontWeight={500} mb={2}>
            Bu işlem geri alınamaz. Tüm bağlı kayıtlar yeni bilgilerle güncellenecek.
          </Typography>
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" color="inherit" onClick={handleCancelChange}>
              Vazgeç
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmChange}>
              Evet, değişiklikleri onaylıyorum
            </Button>
          </Box>
        </ModalBox>
      </Modal>
    </>
  );
};

export default EditCustomerModal;
